/* eslint-disable react-refresh/only-export-components -- hook and component are co-located */
/**
 * TourContext - Guided product tour management
 * Phase 66-01: Tour Infrastructure Foundation
 *
 * Provides context for managing user onboarding tours with React Joyride.
 * Tour step definitions are added in Plan 02.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import posthog from 'posthog-js';
import { useCurrentCompanyMember } from '@/lib/api/companies';
import { useUserTourProgress, useStartTour, useUpdateTourProgress } from '@/lib/api/tour';
import { isAdmin } from '@shared/types/company';
import type { TourType } from '@shared/types/tour';

// =====================================================
// Types
// =====================================================

interface TourContextState {
  /** Whether the tour is currently running */
  run: boolean;
  /** Current step index (0-indexed) */
  stepIndex: number;
  /** Current tour type based on user role */
  tourType: TourType | null;
  /** Whether tour data is loading */
  isLoading: boolean;
  /** Whether the tour has been completed */
  isCompleted: boolean;
  /** Whether the tour has been skipped */
  isSkipped: boolean;
  /** Whether user has seen the tour (completed or skipped) */
  hasSeenTour: boolean;
  /** Whether a tour progress record exists (tour was started at some point) */
  hasProgress: boolean;
  /** Whether the tour is waiting to resume after a navigation */
  pendingResume: boolean;
}

interface TourContextActions {
  /** Start or restart the tour */
  startTour: () => void;
  /** Skip the tour and mark as skipped in database */
  skipTour: () => void;
  /** Mark the tour as completed */
  completeTour: () => void;
  /** Go to a specific step */
  goToStep: (index: number) => void;
  /** Advance to next step */
  nextStep: () => void;
  /** Go back to previous step */
  prevStep: () => void;
  /** Set run state (used by Joyride callback) */
  setRun: (run: boolean) => void;
  /** Set step index (used by Joyride callback) */
  setStepIndex: (index: number) => void;
  /** Signal that the tour should resume after navigation */
  setPendingResume: (pending: boolean) => void;
}

type TourContextValue = TourContextState & TourContextActions;

// =====================================================
// Context
// =====================================================

const TourContext = createContext<TourContextValue | undefined>(undefined);

// =====================================================
// Provider
// =====================================================

interface TourProviderProps {
  children: ReactNode;
}

export function TourProvider({ children }: TourProviderProps) {
  // State
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [pendingResume, setPendingResume] = useState(false);

  // Queries
  const { data: member, isLoading: isMemberLoading } = useCurrentCompanyMember();
  const { data: progress, isLoading: isProgressLoading } = useUserTourProgress();

  // Mutations
  const startTourMutation = useStartTour();
  const updateProgressMutation = useUpdateTourProgress();

  // Determine tour type based on role
  const tourType: TourType | null = member?.role
    ? isAdmin(member.role)
      ? 'admin_onboarding'
      : 'team_onboarding'
    : null;

  const isLoading = isMemberLoading || isProgressLoading;
  const isCompleted = progress?.is_completed ?? false;
  const isSkipped = progress?.is_skipped ?? false;
  const hasSeenTour = isCompleted || isSkipped;
  const hasProgress = !!progress;

  // Track previous tour type to detect role changes
  const prevTourTypeRef = useRef<TourType | null>(null);

  // Detect role change (e.g., promoted from sales_rep to admin)
  useEffect(() => {
    // Skip on initial load (wait until tourType is set)
    if (prevTourTypeRef.current === null) {
      prevTourTypeRef.current = tourType;
      return;
    }

    // Role changed - reset tour for new role
    if (tourType && prevTourTypeRef.current !== tourType) {
      console.log(`[Tour] Role changed from ${prevTourTypeRef.current} to ${tourType}, resetting tour`);

      // Clear old progress and start new tour type
      startTourMutation.mutate(
        { tour_type: tourType },
        {
          onSuccess: () => {
            setStepIndex(0);
            setRun(true);
          },
        }
      );

      prevTourTypeRef.current = tourType;
    }
  }, [tourType, startTourMutation]);

  // Initialize step from database on first load only
  // (subsequent step changes are handled locally via goToStep/setStepIndex)
  const initialSyncDone = useRef(false);
  useEffect(() => {
    if (progress && !isLoading && !initialSyncDone.current) {
      setStepIndex(progress.current_step);
      initialSyncDone.current = true;
    }
  }, [progress, isLoading]);

  // Actions
  const startTour = useCallback(() => {
    // Use team_onboarding as fallback if role not determined yet
    const effectiveTourType = tourType ?? 'team_onboarding';

    // Create or reset progress in database
    startTourMutation.mutate(
      { tour_type: effectiveTourType },
      {
        onSuccess: () => {
          setStepIndex(0);
          setRun(true);

          // Track tour start in PostHog
          posthog.capture('tour_started', {
            tour_type: effectiveTourType,
          });
        },
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependency is used for intentional re-computation
  }, [tourType, member?.role, startTourMutation]);

  const skipTour = useCallback(() => {
    // Track tour skip in PostHog (before mutation to capture current step)
    posthog.capture('tour_skipped', {
      tour_type: tourType,
      skipped_at_step: stepIndex,
    });

    updateProgressMutation.mutate(
      { is_skipped: true },
      {
        onSuccess: () => {
          setRun(false);
        },
      }
    );
  }, [tourType, stepIndex, updateProgressMutation]);

  const completeTour = useCallback(() => {
    updateProgressMutation.mutate(
      { is_completed: true, completed_at: new Date().toISOString() },
      {
        onSuccess: () => {
          setRun(false);

          // Track tour completion in PostHog
          posthog.capture('tour_completed', {
            tour_type: tourType,
            steps_completed: stepIndex + 1,
          });
        },
      }
    );
  }, [tourType, stepIndex, updateProgressMutation]);

  const goToStep = useCallback(
    (index: number) => {
      setStepIndex(index);
      // Persist step progress to database
      updateProgressMutation.mutate({ current_step: index });

      // Track step view in PostHog for funnel analysis
      posthog.capture('tour_step_viewed', {
        tour_type: tourType,
        step_index: index,
      });
    },
    [tourType, updateProgressMutation]
  );

  const nextStep = useCallback(() => {
    setStepIndex((prev) => {
      const next = prev + 1;
      // Persist step progress to database
      updateProgressMutation.mutate({ current_step: next });
      return next;
    });
  }, [updateProgressMutation]);

  const prevStep = useCallback(() => {
    setStepIndex((prev) => {
      const next = Math.max(0, prev - 1);
      // Persist step progress to database
      updateProgressMutation.mutate({ current_step: next });
      return next;
    });
  }, [updateProgressMutation]);

  const value: TourContextValue = {
    // State
    run,
    stepIndex,
    tourType,
    isLoading,
    isCompleted,
    isSkipped,
    hasSeenTour,
    hasProgress,
    pendingResume,
    // Actions
    startTour,
    skipTour,
    completeTour,
    goToStep,
    nextStep,
    prevStep,
    setRun,
    setStepIndex,
    setPendingResume,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

// =====================================================
// Hook
// =====================================================

/**
 * Hook to access tour context
 * Must be used within a TourProvider
 */
export function useTour(): TourContextValue {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}
